import { getAuthHeaders, API_URL } from './config';

/**
 * Generic fetch wrapper for API calls that handles common logic
 */
const fetchApi = async (path: string, options: RequestInit = {}) => {
    const response = await fetch(`${API_URL}${path}`, {
        ...options,
        headers: {
            ...getAuthHeaders(),
            'Content-Type': 'application/json',
            ...options.headers,
        }
    });

    if (!response.ok) {
        let errorBody = "";
        try {
            errorBody = await response.text();
        } catch (e) {
            errorBody = "Could not read error body";
        }
        console.error(`Manager API Error (${response.status}) on ${path}:`, errorBody);

        let parsedError;
        try {
            parsedError = JSON.parse(errorBody);
        } catch (e) {
            parsedError = { message: errorBody };
        }

        throw new Error(parsedError.message || `API Error ${response.status}`);
    }

    if (response.status === 204) return null;
    return response.json();
};

export const managerService = {
    // Club Management
    getMyClub: () => fetchApi('/manager/club'),
    updateMyClub: (data: any) => fetchApi('/manager/club', {
        method: 'POST',
        body: JSON.stringify(data)
    }),

    // Auth / Profile
    getMe: () => fetchApi('/manager/me'),

    // Matches & Stats
    getMatches: () => fetchApi('/manager/matches'),
    getStats: (shopId?: string) => {
        const url = shopId ? `/manager/stats?shopId=${shopId}` : '/manager/stats';
        return fetchApi(url);
    },

    // Shop / Marketplace Management
    updateShop: (shopId: string, data: any) => fetchApi(`/marketplace/shops/${shopId}`, {
        method: 'PUT',
        body: JSON.stringify(data)
    }),

    // Users & Players
    searchUsers: (query: string) => fetchApi(`/manager/users?q=${encodeURIComponent(query)}`),
    getClubPlayers: () => fetchApi('/manager/players'),
    getPlayerDetails: (playerId: string) => fetchApi(`/manager/players/${playerId}`),
    inviteToClub: (playerId: string) => fetchApi(`/manager/players/${playerId}/invite`, { method: 'POST' }),
    inviteAllVisitors: () => fetchApi('/manager/players/invite-all', { method: 'POST' }),

    // Reports
    getReports: () => fetchApi('/manager/reports'),
    resolveReport: (reportId: string, action: 'dismiss' | 'delete') => fetchApi(`/manager/reports/${reportId}`, {
        method: 'POST',
        body: JSON.stringify({ action })
    }),

    // Staff
    getStaff: () => fetchApi('/manager/staff'),
    addStaff: (data: any) => fetchApi('/manager/staff', {
        method: 'POST',
        body: JSON.stringify(data)
    }),
    updateStaff: (id: string, data: any) => fetchApi(`/manager/staff/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data)
    }),
    deleteStaff: (id: string) => fetchApi(`/manager/staff/${id}`, {
        method: 'DELETE'
    })
};
